<template>
  <div class="question-wrong-trend-mobile">
    <div class="chart-card">
      <div class="card-header">
        <span class="card-title">题目错误趋势</span>
        <div class="card-actions">
          <el-tooltip content="导出图片" placement="top">
            <el-button type="text" icon="el-icon-download" @click="handleExport" />
          </el-tooltip>
          <el-tooltip content="全屏显示" placement="top">
            <el-button type="text" icon="el-icon-full-screen" @click="handleFullscreen" />
          </el-tooltip>
        </div>
      </div>
      <div class="chart-container" ref="chart">
        <div class="chart-content">
          <div class="y-axis-label">题目数量</div>
          <div class="y-axis-label right">订正率</div>
        </div>
      </div>
    </div>

    <el-dialog
      :visible.sync="dialogVisible"
      :append-to-body="true"
      :modal-append-to-body="false"
      :close-on-click-modal="false"
      width="95%"
      :destroy-on-close="false"
      :show-close="false"
      class="chart-dialog"
      @closed="handleDialogClosed"
      @open="handleDialogOpen"
    >
      <div slot="title" class="dialog-header">
        <span>题目错误趋势</span>
        <div class="dialog-operations">
          <el-tooltip content="导出图片" placement="top">
            <el-button type="text" icon="el-icon-download" @click="handleExportFullscreen" />
          </el-tooltip>
          <el-tooltip content="关闭" placement="top">
            <el-button type="text" icon="el-icon-close" @click="closeDialog" />
          </el-tooltip>
        </div>
      </div>
      <div class="fullscreen-chart" ref="fullscreenChart"></div>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import request from '@/utils/request'
import { getErrorTagInlineStyle } from '@/constants/errorTypes'

export default {
  name: 'QuestionWrongTrendMobile',
  data() {
    return {
      chart: null,
      fullscreenChart: null,
      dialogVisible: false,
      resizeHandler: null,
      chartData: {
        months: [],
        correct: [],
        undefined: [],
        errorCodes: [],
        errorNames: {},
        errors: {},
        fixRates: []
      },
      baseColors: {
        '正确': '#67C23A',
        '未定义': '#D9D9D9'
      }
    }
  },
  mounted() {
    this.initChart()
    this.fetchData()
    window.addEventListener('resize', this.resizeChart)
  },
  beforeDestroy() {
    if (this.chart) this.chart.dispose()
    if (this.fullscreenChart) this.fullscreenChart.dispose()
    window.removeEventListener('resize', this.resizeChart)
  },
  methods: {
    initChart() {
      this.$nextTick(() => {
        const el = this.$refs.chart
        if (!el) return
        this.chart = echarts.init(el)
        setTimeout(() => { this.chart && this.chart.resize() }, 0)
      })
    },
    resizeChart() {
      this.chart?.resize()
    },
    async fetchData() {
      try {
        const res = await request.get('/api/analysis/question/wrong_trend')
        const data = res?.data || {}
        this.chartData = {
          months: data.months || [],
          correct: data.correct || [],
          undefined: data.undefined || [],
          errorCodes: data.error_codes || [],
          errorNames: data.error_names || {},
          errors: data.errors || {},
          fixRates: data.fix_rates || []
        }
        this.updateChart()
      } catch (error) {
        this.chartData = { months: [], correct: [], undefined: [], errorCodes: [], errorNames: {}, errors: {}, fixRates: [] }
        this.updateChart()
      }
    },
    buildSeries() {
      const series = []
      series.push({
        name: '正确',
        type: 'bar',
        stack: 'total',
        barMaxWidth: 40,
        itemStyle: { color: this.baseColors['正确'], borderRadius: [2, 2, 0, 0] },
        emphasis: { focus: 'series' },
        data: this.chartData.correct || []
      })
      series.push({
        name: '未定义',
        type: 'bar',
        stack: 'total',
        barMaxWidth: 40,
        itemStyle: { color: this.baseColors['未定义'], borderRadius: [2, 2, 0, 0] },
        emphasis: { focus: 'series' },
        data: this.chartData.undefined || []
      })
      for (const code of (this.chartData.errorCodes || [])) {
        const style = getErrorTagInlineStyle(code) || {}
        const color = style.backgroundColor || '#1E90FF'
        series.push({
          name: this.chartData.errorNames?.[code] || code,
          type: 'bar',
          stack: 'total',
          barMaxWidth: 40,
          itemStyle: { color, borderRadius: [2, 2, 0, 0] },
          emphasis: { focus: 'series' },
          data: (this.chartData.errors?.[code] || [])
        })
      }
      series.push({
        name: '订正率',
        type: 'line',
        yAxisIndex: 1,
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        itemStyle: { color: '#1E3A8A' },
        lineStyle: { width: 2, color: '#1E3A8A' },
        data: (this.chartData.fixRates || []).map(v => v == null ? null : Math.round(v * 10000) / 100)
      })
      return series
    },
    updateChart() {
      const el = this.$refs.chart
      if (!el || !this.chart) return
      const option = {
        backgroundColor: '#ffffff',
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'cross' },
          confine: true,
          enterable: false,
          hideDelay: 100,
          backgroundColor: 'rgba(50, 50, 50, 0.9)',
          borderColor: '#333',
          textStyle: {
            color: '#fff',
            fontSize: 11
          },
          formatter: function(params) {
            if (!params || !params.length) return ''
            const title = params[0].name || ''
            
            // 获取标签样式函数
            const getTagStyle = (seriesName) => {
              // 检查是否是基础类型
              if (this.baseColors[seriesName]) {
                const color = this.baseColors[seriesName]
                // 将颜色转换为 rgba 格式用于背景
                const hexToRgba = (hex, alpha) => {
                  const r = parseInt(hex.slice(1, 3), 16)
                  const g = parseInt(hex.slice(3, 5), 16)
                  const b = parseInt(hex.slice(5, 7), 16)
                  return `rgba(${r}, ${g}, ${b}, ${alpha})`
                }
                const bg = hexToRgba(color, 0.2)
                return `display: inline-block; padding: 0px 4px; margin-right: 4px; border-radius: 2px; font-size: 10px; background-color: ${bg}; border: 1px solid ${color}; color: ${color}; line-height: 1.2;`
              }
              
              // 检查是否是错误类型
              const errorCode = this.chartData.errorCodes?.find(code => {
                const errorName = this.chartData.errorNames?.[code] || code
                return errorName === seriesName
              })
              if (errorCode) {
                const style = getErrorTagInlineStyle(errorCode) || {}
                const bgColor = style.backgroundColor || '#1E90FF'
                const borderColor = style.borderColor || bgColor
                const textColor = style.color || bgColor
                // 将背景色转换为半透明
                const hexToRgba = (hex, alpha) => {
                  if (hex.startsWith('rgba')) return hex.replace(/[\d.]+\)$/g, `${alpha})`)
                  if (hex.startsWith('rgb')) return hex.replace('rgb', 'rgba').replace(')', `, ${alpha})`)
                  const r = parseInt(hex.slice(1, 3), 16)
                  const g = parseInt(hex.slice(3, 5), 16)
                  const b = parseInt(hex.slice(5, 7), 16)
                  return `rgba(${r}, ${g}, ${b}, ${alpha})`
                }
                const bg = hexToRgba(bgColor, 0.2)
                return `display: inline-block; padding: 0px 4px; margin-right: 4px; border-radius: 2px; font-size: 10px; background-color: ${bg}; border: 1px solid ${borderColor}; color: ${textColor}; line-height: 1.2;`
              }
              
              return null
            }
            
            let content = ''
            let fixRateText = ''
            params.forEach(p => {
              if (p.seriesName === '订正率') {
                fixRateText = `<div>订正率：${p.value == null ? '-' : p.value + '%'}</div>`
              } else if (p.value != null && p.value > 0) {
                const tagStyle = getTagStyle(p.seriesName)
                if (tagStyle) {
                  content += `<div><span style="${tagStyle}">${p.seriesName}</span>：${p.value}</div>`
                } else {
                  content += `<div>${p.seriesName}：${p.value}</div>`
                }
              }
            })
            if (fixRateText) content += fixRateText
            return `
              <div style="padding: 4px;">
                <div style="font-weight: bold; margin-bottom: 6px;">${title}</div>
                <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.3);">
                  ${content}
                </div>
              </div>
            `
          }.bind(this)
        },
        legend: {
          type: 'plain',
          bottom: 0,
          left: 'center',
          itemGap: 8,
          itemWidth: 10,
          itemHeight: 6,
          textStyle: { fontSize: 10, color: '#666' }
        },
        grid: {
          top: '10%',
          left: '5%',
          right: '5%',
          bottom: '15%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: this.chartData.months || [],
          axisLabel: { fontSize: 10, rotate: 45, interval: 0 },
          axisTick: { show: false }
        },
        yAxis: [
          {
            type: 'value',
            name: '题目数量',
            minInterval: 1,
            position: 'left',
            axisLabel: { formatter: '{value}', margin: 6, fontSize: 10 },
            nameTextStyle: { fontSize: 10, color: '#666' },
            splitLine: { show: true, lineStyle: { type: 'dashed', color: '#E4E7ED' } },
            axisLine: { show: false }
          },
          {
            type: 'value',
            name: '订正率',
            min: 0,
            max: 100,
            interval: 20,
            position: 'right',
            axisLabel: { formatter: '{value}%', margin: 6, fontSize: 10, align: 'right' },
            nameTextStyle: { fontSize: 10, color: '#666' },
            splitLine: { show: false },
            axisLine: { show: false }
          }
        ],
        series: this.buildSeries()
      }
      this.chart.setOption(option, true)
    },
    handleExport() {
      try {
        const dpr = window.devicePixelRatio || 1
        const pixelRatio = Math.min(3, dpr * 1.5)
        const url = this.chart?.getDataURL({ type: 'png', pixelRatio, backgroundColor: '#fff' })
        const a = document.createElement('a')
        a.href = url
        a.download = 'question_wrong_trend.png'
        a.click()
      } catch (e) {}
    },
    handleFullscreen() {
      this.dialogVisible = true
      this.$nextTick(() => {
        this.fullscreenChart = echarts.init(this.$refs.fullscreenChart)
        const option = this.chart?.getOption() || {}
        if (option && option.grid) {
          option.grid = {
            top: '10%',
            left: '5%',
            right: '5%',
            bottom: '15%',
            containLabel: true
          }
        }
        this.fullscreenChart.setOption(option, true)
        setTimeout(() => { this.fullscreenChart && this.fullscreenChart.resize() }, 0)
      })
    },
    handleDialogOpen() {},
    handleDialogClosed() {
      try { this.fullscreenChart && this.fullscreenChart.dispose() } catch (e) {}
      this.fullscreenChart = null
    },
    handleExportFullscreen() {
      try {
        const dpr = window.devicePixelRatio || 1
        const pixelRatio = Math.min(3, dpr * 1.5)
        const url = this.fullscreenChart?.getDataURL({ type: 'png', pixelRatio, backgroundColor: '#fff' })
        const a = document.createElement('a')
        a.href = url
        a.download = 'question_wrong_trend_fullscreen.png'
        a.click()
      } catch (e) {}
    },
    closeDialog() {
      this.dialogVisible = false
    }
  }
}
</script>

<style scoped>
.question-wrong-trend-mobile {
  width: 100%;
  padding: 0;
  box-sizing: border-box;
  margin-bottom: 16px;
}

.chart-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 17px 20px;
  height: 16px;
  box-sizing: content-box;
  background-color: #2E365A;
  color: #ffffff;
  border-radius: 8px 8px 0 0;
}

.card-title {
  font-size: 16px;
  font-weight: 500;
  color: white;
}

.card-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
}

.card-actions .el-button {
  color: #ffffff;
  font-size: 16px;
  padding: 4px;
  margin-left: 8px;
}

.card-actions .el-button:hover {
  color: #409EFF;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

.chart-container {
  position: relative;
  width: 100%;
  height: 300px;
  padding: 0;
  box-sizing: border-box;
}
.chart-content .y-axis-label {
  position: absolute;
  top: 8px;
  left: 8px;
  font-size: 12px;
  color: #909399;
}
.chart-content .y-axis-label.right {
  right: 8px;
  left: auto;
}

/* 全屏弹窗样式 */
.chart-dialog :deep(.el-dialog__header) {
  padding: 0;
  margin: 0;
}
.chart-dialog :deep(.el-dialog__headerbtn) {
  display: none;
}
.chart-dialog :deep(.el-dialog__body) {
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 17px 20px;
  height: 16px;
  box-sizing: content-box;
  background-color: #2E365A;
  color: #ffffff;
  border-bottom: none;
}
.dialog-operations {
  display: flex;
  gap: 8px;
}
.dialog-operations .el-button {
  color: #ffffff;
  padding: 4px;
}
.dialog-operations .el-button:hover {
  color: #BD6C73;
}
.fullscreen-chart {
  width: 100%;
  height: 500px;
  padding: 20px;
  box-sizing: border-box;
}

/* 移动端优化 */
@media (max-width: 480px) {
  .question-wrong-trend-mobile {
    padding: 0;
    margin-bottom: 12px;
  }
  .chart-container {
    height: 300px;
    padding: 0;
  }
  .card-header {
    padding: 12px 16px;
  }
  .card-title {
    font-size: 14px;
  }
  .card-actions .el-button {
    font-size: 14px;
    padding: 2px;
    margin-left: 4px;
  }
  .fullscreen-chart {
    height: 400px;
    padding: 16px;
  }
}
</style>